/**
 * Renderer for radial (polar) mazes.
 *
 * Draws concentric ring walls via ctx.arc() and radial spoke walls
 * via ctx.lineTo(). Supports dark/light mode, path drawing in polar
 * space, entrance/exit emoji positioning, and PNG export.
 */

import type { RadialMazeState, PathSegment } from '../types/maze-types';
import { MAZE_COLORS, GAME_COLORS, getMazeBackground, getZoneColors } from '@/contexts/games/constants/colors';
import { MAZE_EMOJIS } from '../config/mazeEmojis';
import { polarToCartesian } from './polarUtils';

const TWO_PI = 2 * Math.PI;

export class RadialMazeRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Cannot get 2D context from canvas');
    }
    this.ctx = ctx;
  }

  /**
   * Full render of the radial maze.
   */
  render(state: RadialMazeState, includePath = true): void {
    this.clearCanvas(state);
    this.drawWalls(state);
    if (includePath) {
      this.drawPath(state);
    }
    this.drawZones(state);
  }

  /**
   * Create a PNG data URL for download (without user path).
   */
  createDownloadImage(state: RadialMazeState): string {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.canvas.width;
    tempCanvas.height = this.canvas.height;

    const tempRenderer = new RadialMazeRenderer(tempCanvas);
    tempRenderer.render(state, false);

    return tempCanvas.toDataURL('image/png');
  }

  // ---------------------------------------------------------------------------
  // Canvas clear
  // ---------------------------------------------------------------------------

  private clearCanvas(state: RadialMazeState): void {
    this.ctx.fillStyle = getMazeBackground(state.isDarkMode ?? false);
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  // ---------------------------------------------------------------------------
  // Wall drawing
  // ---------------------------------------------------------------------------

  private drawWalls(state: RadialMazeState): void {
    const { ctx } = this;
    const { center, ringWidth, cells, sectorsPerRing, rings } = state;

    ctx.strokeStyle = state.isDarkMode
      ? MAZE_COLORS.wallDark
      : MAZE_COLORS.wallLight;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (let r = 0; r < rings; r++) {
      const sectorCount = sectorsPerRing[r];
      const sectorAngle = TWO_PI / sectorCount;
      const innerRadius = r * ringWidth;
      const outerRadius = (r + 1) * ringWidth;

      for (let s = 0; s < sectorCount; s++) {
        const cell = cells[r][s];
        const startAngle = s * sectorAngle;
        const endAngle = (s + 1) * sectorAngle;

        // Outward wall — arc at outerRadius
        if (cell.walls.outward) {
          ctx.beginPath();
          ctx.arc(center.x, center.y, outerRadius, startAngle, endAngle);
          ctx.stroke();
        }

        // Inward wall — arc at innerRadius (skip centre cell which has no inward)
        if (r > 0 && cell.walls.inward) {
          // Only draw if the inner ring has a different sector count,
          // otherwise the outward wall of the inner cell covers it.
          // To avoid duplicates: draw inward wall only when sector counts differ.
          const innerSectorCount = sectorsPerRing[r - 1];
          if (innerSectorCount !== sectorCount) {
            ctx.beginPath();
            ctx.arc(center.x, center.y, innerRadius, startAngle, endAngle);
            ctx.stroke();
          }
        }

        // Clockwise radial wall — spoke at endAngle from innerRadius to outerRadius
        if (cell.walls.cw) {
          const inner = polarToCartesian(innerRadius, endAngle, center.x, center.y);
          const outer = polarToCartesian(outerRadius, endAngle, center.x, center.y);
          ctx.beginPath();
          ctx.moveTo(inner.x, inner.y);
          ctx.lineTo(outer.x, outer.y);
          ctx.stroke();
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Path drawing
  // ---------------------------------------------------------------------------

  private drawPath(state: RadialMazeState): void {
    state.pathSegments.forEach((segment: PathSegment) => {
      if (segment.points.length < 2) return;

      this.ctx.strokeStyle = segment.color;
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.beginPath();

      this.ctx.moveTo(segment.points[0].x, segment.points[0].y);
      for (let i = 1; i < segment.points.length; i++) {
        this.ctx.lineTo(segment.points[i].x, segment.points[i].y);
      }
      this.ctx.stroke();
    });
  }

  // ---------------------------------------------------------------------------
  // Entrance / Exit zones
  // ---------------------------------------------------------------------------

  private drawZones(state: RadialMazeState): void {
    this.drawZoneEmoji(
      state,
      state.entrance.ring,
      state.entrance.sector,
      MAZE_EMOJIS.entrance,
      true,
    );
    this.drawZoneEmoji(
      state,
      state.exit.ring,
      state.exit.sector,
      MAZE_EMOJIS.exit,
      false,
    );
  }

  /**
   * Position an emoji at the centre of the given cell.
   * For the entrance (outermost ring), the emoji is nudged outward
   * so it sits just outside the maze boundary.
   */
  private drawZoneEmoji(
    state: RadialMazeState,
    ring: number,
    sector: number,
    emoji: string,
    isEntrance: boolean,
  ): void {
    const { center, ringWidth, sectorsPerRing } = state;
    const sectorCount = sectorsPerRing[ring];
    const sectorAngle = TWO_PI / sectorCount;
    const midAngle = (sector + 0.5) * sectorAngle;

    let radius: number;
    if (isEntrance) {
      // Place at the centre of the outermost ring cell (not beyond the boundary)
      radius = (ring + 0.5) * ringWidth;
    } else if (ring === 0) {
      // Centre cell
      radius = 0;
    } else {
      radius = (ring + 0.5) * ringWidth;
    }

    const pos = polarToCartesian(radius, midAngle, center.x, center.y);
    // Glyph sized to the RING, clamped for legibility — the old 20 px floor spilled
    // over the corridor walls on a phone where a ring is ~12 px.
    const fontSize = Math.max(10, Math.min(24, ringWidth * 0.8));

    // Disc behind the emoji — never wider than the corridor — with the fleet's
    // marker rim (blue entry / amber exit, brighter on dark).
    const bgRadius = Math.min(fontSize * 0.6, ringWidth * 0.45);
    const zc = getZoneColors(state.isDarkMode ?? false);
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(pos.x, pos.y, bgRadius, 0, TWO_PI);
    this.ctx.fillStyle = state.isDarkMode ? MAZE_COLORS.badgeFillDark : MAZE_COLORS.badgeFillLight;
    this.ctx.fill();
    this.ctx.lineWidth = Math.max(2, bgRadius * 0.2);
    this.ctx.strokeStyle = emoji === MAZE_EMOJIS.entrance ? zc.entryRing : zc.exitRing;
    this.ctx.stroke();
    this.ctx.restore();

    // Draw emoji
    this.ctx.font = `${fontSize}px Arial`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(emoji, pos.x, pos.y);
  }

  // ---------------------------------------------------------------------------
  // SVG export (for print / download)
  // ---------------------------------------------------------------------------

  /**
   * Convert the radial maze to an SVG string for print/download.
   * Uses proper SVG arc commands to preserve geometry at any resolution.
   */
  static toSVG(
    state: RadialMazeState,
    options: {
      width: number;
      height: number;
      includePath?: boolean;
      includeZones?: boolean;
      isDarkMode?: boolean;
    },
  ): string {
    const { center, ringWidth, cells, sectorsPerRing, rings } = state;
    const darkMode = options.isDarkMode ?? state.isDarkMode ?? false;
    const wallColor = darkMode ? MAZE_COLORS.wallDark : MAZE_COLORS.wallLight;
    const bgColor = darkMode ? MAZE_COLORS.backgroundDark : MAZE_COLORS.backgroundLight;

    const parts: string[] = [];

    parts.push(
      `<?xml version="1.0" encoding="UTF-8"?>`,
      `<svg xmlns="http://www.w3.org/2000/svg" width="${options.width}" height="${options.height}" viewBox="0 0 ${state.canvasSize.width} ${state.canvasSize.height}">`,
      `<rect width="${state.canvasSize.width}" height="${state.canvasSize.height}" fill="${bgColor}"/>`,
    );

    // Walls group
    parts.push(
      `<g stroke="${wallColor}" stroke-width="4" fill="none" stroke-linecap="round">`,
    );

    for (let r = 0; r < rings; r++) {
      const sectorCount = sectorsPerRing[r];
      const sectorAngle = TWO_PI / sectorCount;
      const innerRadius = r * ringWidth;
      const outerRadius = (r + 1) * ringWidth;

      for (let s = 0; s < sectorCount; s++) {
        const cell = cells[r][s];
        const startAngle = s * sectorAngle;
        const endAngle = (s + 1) * sectorAngle;

        // Outward arc wall
        if (cell.walls.outward) {
          parts.push(
            svgArc(center.x, center.y, outerRadius, startAngle, endAngle),
          );
        }

        // Inward arc wall (only when sector counts differ to avoid duplicates)
        if (r > 0 && cell.walls.inward) {
          const innerSectorCount = sectorsPerRing[r - 1];
          if (innerSectorCount !== sectorCount) {
            parts.push(
              svgArc(center.x, center.y, innerRadius, startAngle, endAngle),
            );
          }
        }

        // Clockwise radial wall
        if (cell.walls.cw) {
          const inner = polarToCartesian(innerRadius, endAngle, center.x, center.y);
          const outer = polarToCartesian(outerRadius, endAngle, center.x, center.y);
          parts.push(
            `<line x1="${inner.x}" y1="${inner.y}" x2="${outer.x}" y2="${outer.y}"/>`,
          );
        }
      }
    }

    parts.push(`</g>`);

    // Path
    if (options.includePath !== false && state.pathSegments.length > 0) {
      parts.push(
        `<g stroke="${GAME_COLORS.blue}" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.6">`,
      );
      for (const segment of state.pathSegments) {
        if (segment.points.length < 2) continue;
        let d = `M ${segment.points[0].x},${segment.points[0].y}`;
        for (let i = 1; i < segment.points.length; i++) {
          d += ` L ${segment.points[i].x},${segment.points[i].y}`;
        }
        parts.push(`<path d="${d}"/>`);
      }
      parts.push(`</g>`);
    }

    // Zone emojis
    if (options.includeZones !== false) {
      const fontSize = Math.max(10, Math.min(24, ringWidth * 0.8));

      // Entrance — placed at centre of outermost ring cell
      const eSectorCount = sectorsPerRing[state.entrance.ring];
      const eSectorAngle = TWO_PI / eSectorCount;
      const eMidAngle = (state.entrance.sector + 0.5) * eSectorAngle;
      const eRadius = (state.entrance.ring + 0.5) * ringWidth;
      const ePos = polarToCartesian(eRadius, eMidAngle, center.x, center.y);

      parts.push(
        `<text x="${ePos.x}" y="${ePos.y}" text-anchor="middle" dominant-baseline="middle" font-size="${fontSize}" font-family="Arial">${MAZE_EMOJIS.entrance}</text>`,
      );

      // Exit (centre)
      parts.push(
        `<text x="${center.x}" y="${center.y}" text-anchor="middle" dominant-baseline="middle" font-size="${fontSize}" font-family="Arial">${MAZE_EMOJIS.exit}</text>`,
      );
    }

    parts.push(`</svg>`);
    return parts.join('\n');
  }
}

// ---------------------------------------------------------------------------
// SVG arc helper
// ---------------------------------------------------------------------------

/**
 * Create an SVG `<path>` element for a circular arc.
 * Uses the SVG arc command (A) with correct large-arc and sweep flags.
 */
function svgArc(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  const start = polarToCartesian(radius, startAngle, cx, cy);
  const end = polarToCartesian(radius, endAngle, cx, cy);

  // Determine if the arc spans more than π radians
  let sweep = endAngle - startAngle;
  if (sweep < 0) sweep += TWO_PI;
  const largeArc = sweep > Math.PI ? 1 : 0;

  // SVG arc: sweep-flag 1 = clockwise (positive y is down in SVG/canvas)
  return `<path d="M ${start.x},${start.y} A ${radius},${radius} 0 ${largeArc} 1 ${end.x},${end.y}"/>`;
}
